import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { IBaseResponse, ILateBloodDraw, ILateBloodDrawFilter } from '@nkr/models/index';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';

interface IRetVal {
    list: ILateBloodDraw[],
    loading: boolean,
    error: string,
    updateBloodDrawDate: (serologyTrackingId: number, bloodDrawDate: string) => Promise<boolean>
}

// The API returns its failure reason as the response body, so surface that rather than the axios
// Error - setError must only ever hold a string, or the component renders an object as a React child.
const toMessage = (err: any, fallback: string): string => {
    const body = err && err.response ? err.response.data : null;

    if (typeof body === 'string' && body != '') {
        return body;
    }

    return err && err.message ? err.message : fallback;
};

const useBloodDrawsOutsideWindow = (filter?: ILateBloodDrawFilter): IRetVal => {
    const { dispatch } = useAppHookContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<ILateBloodDraw[]>([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');

        let config = {
            url: `${__API__}/report/blood-draws-outside-window/search`,
            data: filter
        };

        try {
            const response: IBaseResponse = await dispatch(AuthActionCreators.apiPost(config));

            if (response && response.data.results && response.data.success) {
                setList(response.data.results);
            } else {
                setError('Unable to load blood draws outside window');
                setList([]);
            }
        } catch (err) {
            setList([]);
            setError(toMessage(err, 'Unable to load blood draws outside window'));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        if (filter) {
            fetchData();
        }
    }, [filter]);

    // Refetch rather than patch the row: a correction can move a row out of the 5-7 window entirely, or leave
    // it in with a new delta, and the server is the only thing that knows which.
    const updateBloodDrawDate = async (serologyTrackingId: number, bloodDrawDate: string): Promise<boolean> => {
        setError('');
        setLoading(true);

        let options = {
            url: `${__API__}/report/blood-draws-outside-window/blood-draw-date`,
            data: {
                serologyTrackingId,
                bloodDrawDate
            }
        };

        try {
            const r: any = await dispatch(AuthActionCreators.apiPut(options));

            if (r && r.data && r.data.success) {
                // The row can legitimately disappear on refetch, so the toast is the only confirmation
                // the admin gets that the save took.
                toast.success('Blood Draw Date Updated Successfully');

                await fetchData();

                return true;
            }

            setError(toMessage(r, 'An error occurred while updating the blood draw date.'));
            setLoading(false);

            return false;
        } catch (err) {
            setError(toMessage(err, 'An error occurred while updating the blood draw date.'));
            setLoading(false);

            return false;
        }
    };

    return { list, loading, error, updateBloodDrawDate }
}

export default useBloodDrawsOutsideWindow;
